import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useDraftTopic, useCreateTopic } from "../../hooks/useTopics";

export function NewTopicDialog({ open, onOpenChange, parentConversationId, projectId, onTopicCreated }) {
  const [step, setStep] = useState("hint");
  const [hint, setHint] = useState("");
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [error, setError] = useState("");

  const draftTopic = useDraftTopic();
  const createTopic = useCreateTopic(projectId);

  const reset = () => {
    setStep("hint");
    setHint("");
    setTitle("");
    setBrief("");
    setError("");
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDraft = async () => {
    if (!hint.trim() || !parentConversationId) return;
    setError("");
    try {
      const result = await draftTopic.mutateAsync({ parentId: parentConversationId, topicHint: hint.trim() });
      setTitle(result.title);
      setBrief(result.brief);
      setStep("review");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo generar el contexto. Inténtalo de nuevo.");
    }
  };

  const handleCreate = async () => {
    const conversation = await createTopic.mutateAsync({
      parentId: parentConversationId,
      title: title.trim() || "Nuevo tema",
      brief: brief.trim(),
    });
    handleOpenChange(false);
    onTopicCreated(conversation.id);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="new-topic-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo tema</DialogTitle>
          <DialogDescription>
            {step === "hint"
              ? "¿De qué quieres que trate este tema? El agente arma un contexto inicial a partir de esta conversación."
              : "Revisa el contexto antes de crear el tema. Puedes editarlo."}
          </DialogDescription>
        </DialogHeader>

        {step === "hint" && (
          <Textarea
            data-testid="topic-hint-textarea"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Ej. Facturación y cobranza"
            className="min-h-[80px]"
          />
        )}

        {error && (
          <p data-testid="topic-draft-error" role="alert" className="text-sm text-red-500">
            {error}
          </p>
        )}

        {step === "review" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Título</label>
              <Input
                data-testid="topic-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Contexto delegado</label>
              <Textarea
                data-testid="topic-brief-textarea"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                className="min-h-[160px]"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "hint" && (
            <Button
              data-testid="topic-draft-button"
              onClick={handleDraft}
              disabled={!hint.trim() || draftTopic.isPending}
            >
              {draftTopic.isPending ? "Generando..." : error ? "Reintentar" : "Generar contexto"}
            </Button>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={() => setStep("hint")}>
                Volver
              </Button>
              <Button
                data-testid="topic-create-button"
                onClick={handleCreate}
                disabled={createTopic.isPending}
              >
                {createTopic.isPending ? "Creando..." : "Crear tema"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
